import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import BookCallModal from "../../components/feature/BookCallModal";

const caseStudies = [
  {
    id: "home-services",
    industry: "Home Services",
    icon: "ri-hammer-line",
    tag: "Field Services",
    headline: "AI Call Answering Turns Missed Calls Into Booked Jobs",
    image: "https://public.readdy.ai/ai/img_res/5bf1342ab11b715f2f4ddcd9efb6abdb.jpg",
    problem: {
      title: "Missed Calls During Active Jobs",
      text: "Their 4-person crew was constantly in the field. Every unanswered inbound call was a potential job going straight to a competitor.",
    },
    system: {
      title: "AI Call Answering + Instant SMS Follow-Up",
      bullets: [
        "AI voice agent handles all inbound calls 24/7",
        "Captures caller info and job type automatically",
        "Sends an instant SMS to keep the lead warm",
        "Routes qualified leads directly to the owner",
      ],
    },
    result: {
      metric: "+32%",
      label: "Booked Jobs",
      supporting: [
        { val: "100%", desc: "Call answer rate" },
        { val: "2 min", desc: "Avg. response time" },
        { val: "30 days", desc: "To see results" },
      ],
      quote: "We used to lose 5–6 jobs a week to voicemail. Now every call gets handled and we're booked out 3 weeks.",
      author: "Operations Manager, HVAC Company",
    },
  },
  {
    id: "medspa",
    industry: "Medical / Medspa",
    icon: "ri-heart-pulse-line",
    tag: "Healthcare & Aesthetics",
    headline: "Automated Follow-Up Rebuilt a Medspa's Retention Machine",
    image: "https://readdy.ai/api/search-image?query=modern%20luxury%20medspa%20aesthetic%20clinic%20interior%20minimal%20white%20clean%20bright%20treatment%20room%20elegant%20comfortable%20serene%20wellness%20beauty%20professional%20lighting%20high%20end%20interior%20design%20commercial%20photography&width=900&height=560&seq=cs-medspa-1&orientation=landscape",
    problem: {
      title: "Clients Weren't Coming Back",
      text: "First visits were strong, but rebooking was nearly zero. No one was following up after appointments and retention was falling every quarter.",
    },
    system: {
      title: "Automated Follow-Up & Rebooking Sequences",
      bullets: [
        "Post-visit SMS sequence sent within 2 hours",
        "Personalized rebooking links tied to their treatment",
        "30-day and 60-day reactivation campaigns",
        "Staff only touches warm, ready-to-book leads",
      ],
    },
    result: {
      metric: "+40%",
      label: "Appointment Retention",
      supporting: [
        { val: "3x", desc: "Rebooking rate" },
        { val: "60%", desc: "Less admin time" },
        { val: "45 days", desc: "Full deployment" },
      ],
      quote: "We went from a 20% rebooking rate to over 60%. This system basically runs our entire retention strategy.",
      author: "Practice Owner, Medical Medspa Group",
    },
  },
  {
    id: "legal",
    industry: "Legal",
    icon: "ri-scales-3-line",
    tag: "Professional Services",
    headline: "AI Intake Workflow Doubled Qualified Client Conversions",
    image: "https://readdy.ai/api/search-image?query=modern%20premium%20law%20firm%20office%20clean%20minimal%20elegant%20dark%20wood%20conference%20table%20natural%20light%20contemporary%20corporate%20interior%20design%20professional%20legal%20setting%20high%20end%20commercial%20photography&width=900&height=560&seq=cs-legal-1&orientation=landscape",
    problem: {
      title: "Intake Was Manual, Slow, and Leaking Revenue",
      text: "Potential clients filled out a form and waited 24–48 hours to hear back. By then, half had already retained another firm.",
    },
    system: {
      title: "AI Qualification Workflow + Instant Routing",
      bullets: [
        "AI screens and qualifies leads within 60 seconds",
        "High-value cases immediately routed to attorneys",
        "Unqualified leads auto-responded with referrals",
        "Full intake data synced to CRM automatically",
      ],
    },
    result: {
      metric: "+25%",
      label: "Client Conversion Rate",
      supporting: [
        { val: "4x", desc: "Faster intake" },
        { val: "70%", desc: "Reduction in unqualified calls" },
        { val: "60 days", desc: "ROI achieved" },
      ],
      quote: "We stopped losing cases to response time. Our attorneys now only speak to leads that are already qualified.",
      author: "Managing Partner, Personal Injury Firm",
    },
  },
];

const CaseStudiesPage = () => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen font-sans bg-white">
      <Navbar onBookCall={() => setModalOpen(true)} />

      {/* Page Hero */}
      <section className="relative bg-charcoal pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20charcoal%20geometric%20grid%20lines%20blueprint%20subtle%20minimal%20enterprise%20technical%20background%20architecture%20clean%20dark%20gray%20texture%20pattern&width=1440&height=420&seq=cs-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal/80"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="inline-block bg-sapphire/20 text-sapphire text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
            Case Studies
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl mb-5">
            Proven Systems.<br />Measurable Results.
          </h1>
          <p className="text-white/55 text-lg leading-relaxed max-w-xl">
            Real businesses. Real problems. Real numbers. See how our AI systems drive revenue across service industries.
          </p>
          {/* Stats bar */}
          <div className="flex flex-wrap items-center gap-10 mt-12">
            {[
              { val: "3", label: "Industries Served" },
              { val: "+32%", label: "Avg. Conversion Lift" },
              { val: "30 Days", label: "Avg. Time to Results" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-extrabold text-white">{s.val}</div>
                <div className="text-xs text-white/40 mt-1 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-32">
          {caseStudies.map((cs, i) => (
            <article key={cs.id} id={cs.id}>
              {/* Industry Tag */}
              <div className="flex items-center gap-3 mb-8">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-sapphire/10">
                  <i className={`${cs.icon} text-sapphire text-base`}></i>
                </div>
                <span className="text-xs font-bold text-sapphire uppercase tracking-widest">{cs.industry}</span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{cs.tag}</span>
              </div>

              <h2 className="text-3xl lg:text-4xl font-extrabold text-charcoal leading-tight mb-10 max-w-3xl">
                {cs.headline}
              </h2>

              {/* Image */}
              <div className="relative w-full h-72 lg:h-[420px] rounded-2xl overflow-hidden mb-12">
                <img src={cs.image} alt={cs.industry} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent"></div>
                {/* Result badge on image */}
                <div className="absolute bottom-6 right-6 bg-white rounded-xl px-6 py-4 text-center">
                  <div className="text-3xl font-extrabold text-charcoal">{cs.result.metric}</div>
                  <div className="text-xs text-gray-500 mt-0.5 font-medium">{cs.result.label}</div>
                </div>
              </div>

              {/* 3-column content */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Problem */}
                <div className="bg-red-50 border border-red-100 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-error-warning-line text-red-500 text-base"></i>
                    </span>
                    <span className="text-xs font-bold text-red-500 uppercase tracking-widest">Problem</span>
                  </div>
                  <h3 className="text-sm font-bold text-charcoal mb-2">{cs.problem.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{cs.problem.text}</p>
                </div>

                {/* System */}
                <div className="bg-sapphire/5 border border-sapphire/15 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-settings-4-line text-sapphire text-base"></i>
                    </span>
                    <span className="text-xs font-bold text-sapphire uppercase tracking-widest">System</span>
                  </div>
                  <h3 className="text-sm font-bold text-charcoal mb-3">{cs.system.title}</h3>
                  <ul className="space-y-2">
                    {cs.system.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-sapphire text-xs font-bold"></i>
                        </span>
                        <span className="text-xs text-gray-700 leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Result */}
                <div className="bg-charcoal rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-line-chart-line text-green-400 text-base"></i>
                    </span>
                    <span className="text-xs font-bold text-green-400 uppercase tracking-widest">Result</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    {cs.result.supporting.map((s) => (
                      <div key={s.desc} className="text-center">
                        <div className="text-lg font-extrabold text-white">{s.val}</div>
                        <div className="text-xs text-white/40 mt-0.5 leading-tight">{s.desc}</div>
                      </div>
                    ))}
                  </div>
                  <blockquote className="border-l-2 border-sapphire pl-3">
                    <p className="text-xs text-white/70 italic leading-relaxed mb-2">&ldquo;{cs.result.quote}&rdquo;</p>
                    <cite className="text-xs text-white/40 not-italic">{cs.result.author}</cite>
                  </blockquote>
                </div>
              </div>

              {/* Divider */}
              {i < caseStudies.length - 1 && (
                <div className="mt-16 border-t border-gray-100"></div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-block bg-sapphire/20 text-sapphire text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
            Your Turn
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-5">
            You Could Be Next
          </h2>
          <p className="text-white/50 text-base max-w-lg mx-auto mb-10">
            These results aren&apos;t outliers. They&apos;re what happens when the right systems are built for your business. Let&apos;s map yours out.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="bg-sapphire hover:bg-deep-sapphire text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            Book Strategy Call
          </button>
        </div>
      </section>

      <Footer onBookCall={() => setModalOpen(true)} />
      <BookCallModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default CaseStudiesPage;
